import { useMemo } from 'react';
import type { TrafficSource } from '@booking/shared';

const STORAGE_KEY = 'alena_traffic_source';

/**
 * Captures UTM params + click IDs from the landing URL and persists them
 * across the booking funnel via sessionStorage. Reads first from storage
 * (so we don't lose attribution on navigation), then merges with the
 * current URL's params (in case the user came in fresh).
 *
 * Call this once at app root.
 */
export function useTrafficSource(): TrafficSource {
  return useMemo(() => {
    const stored = readFromStorage();
    const fromUrl = readFromUrl();

    // First landing wins — once a source is captured, don't overwrite it
    // unless the user comes back with a fresh ad click (new fbclid/gclid).
    const isNewClick = !!(fromUrl.fbclid || fromUrl.gclid || fromUrl.ttclid);
    const merged: TrafficSource = isNewClick || !stored ? fromUrl : stored;

    // Always refresh fbp from cookie (browser Pixel may have set it after landing)
    merged.fbp = readFbpCookie() ?? merged.fbp;
    /* lo mismo con la de TikTok: el píxel la pone después de cargar */
    merged.ttp = readCookie('_ttp') ?? merged.ttp;

    writeToStorage(merged);
    return merged;
  }, []);
}

function readFromUrl(): TrafficSource {
  const p = new URLSearchParams(window.location.search);
  const fbclid = p.get('fbclid') ?? undefined;

  return {
    utmSource: p.get('utm_source') ?? undefined,
    utmMedium: p.get('utm_medium') ?? undefined,
    utmCampaign: p.get('utm_campaign') ?? undefined,
    utmContent: p.get('utm_content') ?? undefined,
    utmTerm: p.get('utm_term') ?? undefined,
    fbclid,
    gclid: p.get('gclid') ?? undefined,
    ttclid: p.get('ttclid') ?? undefined,
    referrer: document.referrer || undefined,
    landingUrl: window.location.href,
    landedAt: new Date().toISOString(),
  };
}

function readFromStorage(): TrafficSource | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TrafficSource) : null;
  } catch {
    return null;
  }
}

function writeToStorage(source: TrafficSource) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(source));
  } catch {
    // Safari ITP in iframes can throw; silently ignore.
  }
}

function readFbpCookie(): string | undefined {
  return readCookie('_fbp');
}

function readCookie(nombre: string): string | undefined {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + nombre + '=([^;]+)'));
  return match?.[1];
}
